import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useGameRequestStatus } from "@/hooks/useGameRequestStatus";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { parseGenres } from "@/data/gameGenres";
import { GameRequestButton } from "@/components/GameRequestButton";
import { EventBanner } from "@/components/EventBanner";
import { Key, Calendar, Image, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface Game {
  id: string;
  title: string;
  description: string;
  genres: string[];
  cover_image_url: string;
  screenshots: string[];
  steam_url: string;
  price: number;
  available_keys: number;
  total_keys: number;
  created_at: string;
  release_date?: string;
  highlight_id: number;
  highlight_end_date?: string;
  is_published: boolean;
  allow_requests: boolean;
  developer_id: string;
  developer: {
    display_name: string;
    studio_name?: string;
    studio_logo_url?: string;
    studio_slug?: string;
  };
}

export default function CreatorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [isRequestingKey, setIsRequestingKey] = useState(false);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [requestMessage, setRequestMessage] = useState("");
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideProgress, setSlideProgress] = useState(0); // Progress from 0 to 100
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Animation states
  const [isFeaturedVisible, setIsFeaturedVisible] = useState(false);
  const [isComingSoonVisible, setIsComingSoonVisible] = useState(false);
  const [isRecentlyVisible, setIsRecentlyVisible] = useState(false);

  // Refs for sections
  const featuredRef = useRef<HTMLDivElement>(null);
  const comingSoonRef = useRef<HTMLDivElement>(null);
  const recentlyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user?.id) {
      fetchGames();
    }
  }, [user?.id]);

  // Auto-play du slider avec progression
  useEffect(() => {
    const level4Games = getHighlightLevel4Games();
    if (level4Games.length > 1) {
      setSlideProgress(0); // Reset progress when slide changes

      const progressInterval = setInterval(() => {
        setSlideProgress((prev) => {
          if (prev >= 100) {
            setCurrentSlide((current) => (current + 1) % level4Games.length);
            return 0;
          }
          return prev + 2; // Increment by 2% every 100ms (5 seconds total = 5000ms / 50 intervals)
        });
      }, 100); // Update every 100ms

      return () => clearInterval(progressInterval);
    }
  }, [games, currentSlide]);

  // Intersection observers for animations
  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    // Featured games observer
    if (featuredRef.current) {
      const observer1 = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsFeaturedVisible(true);
          }
        },
        { threshold: 0.1 }
      );
      observer1.observe(featuredRef.current);
      observers.push(observer1);
    }

    // Coming soon observer
    if (comingSoonRef.current) {
      const observer2 = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsComingSoonVisible(true);
          }
        },
        { threshold: 0.1 }
      );
      observer2.observe(comingSoonRef.current);
      observers.push(observer2);
    }

    // Recently released observer
    if (recentlyRef.current) {
      const observer3 = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsRecentlyVisible(true);
          }
        },
        { threshold: 0.1 }
      );
      observer3.observe(recentlyRef.current);
      observers.push(observer3);
    }

    return () => {
      observers.forEach(obs => obs.disconnect());
    };
  }, [games.length]);

  const fetchGames = async () => {
    const { data } = await supabase
      .from("games")
      .select(`
        *,
        developer:profiles(
          display_name,
          studio_name,
          studio_logo_url,
          studio_slug
        )
      `)
      .eq("is_published", true)
      .order("created_at", { ascending: false });

    // Parser les genres pour chaque jeu
    const gamesWithGenres = (data || []).map(game => ({
      ...game,
      genres: parseGenres(game.genre || ""),
      highlight_id: (game as any).highlight_id || 1,
      highlight_end_date: (game as any).highlight_end_date || null,
      is_published: (game as any).is_published || false,
      allow_requests: (game as any).allow_requests !== false,
      price: (game as any).price || 0
    }));
    setGames(gamesWithGenres);
  };

  // Fonctions pour organiser les jeux par niveau de highlight
  const getHighlightLevel4Games = () => {
    return games.filter(game => game.highlight_id === 4);
  };

  const getHighlightLevel3Games = () => {
    return games.filter(game => game.highlight_id === 3);
  };

  const getHighlightLevel2Games = () => {
    return games.filter(game => game.highlight_id === 2);
  };

  const getComingSoonGames = () => {
    const today = new Date();
    return games
      .filter(game => {
        if (!game.release_date) return false;
        const releaseDate = new Date(game.release_date);
        return releaseDate > today;
      })
      .sort((a, b) => {
        const dateA = new Date(a.release_date!).getTime();
        const dateB = new Date(b.release_date!).getTime();
        return dateA - dateB; // Plus proche en premier
      });
  };

  const getRecentlyReleasedGames = () => {
    const today = new Date();
    return games
      .filter(game => {
        if (!game.release_date) return false;
        const releaseDate = new Date(game.release_date);
        return releaseDate <= today;
      })
      .sort((a, b) => {
        const dateA = new Date(a.release_date!).getTime();
        const dateB = new Date(b.release_date!).getTime();
        return dateB - dateA; // Plus récent en premier
      });
  };

  const nextSlide = () => {
    const level4Games = getHighlightLevel4Games();
    setCurrentSlide((prev) => (prev + 1) % level4Games.length);
    setSlideProgress(0);
  };

  const prevSlide = () => {
    const level4Games = getHighlightLevel4Games();
    setCurrentSlide((prev) => (prev - 1 + level4Games.length) % level4Games.length);
    setSlideProgress(0);
  };

  // Swipe handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };

  const handleRequestKey = (game: Game) => {
    setSelectedGame(game);
    setIsRequestingKey(true);
    setRequestMessage("");
  };

  const submitKeyRequest = async () => {
    if (!user || !selectedGame) return;

    setIsSubmittingRequest(true);
    try {
      const { error } = await supabase
        .from("key_requests")
        .insert({
          game_id: selectedGame.id,
          creator_id: user.id,
          developer_id: selectedGame.developer_id,
          message: requestMessage.trim() || null
        });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success("Key request submitted successfully!");
        setIsRequestingKey(false);
        setRequestMessage("");
        setSelectedGame(null);
      }
    } catch (error) {
      console.error('Error requesting key:', error);
      toast.error("Error submitting key request");
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const level4Games = getHighlightLevel4Games();
  const level3Games = getHighlightLevel3Games();
  const level2Games = getHighlightLevel2Games();
  const comingSoonGames = getComingSoonGames();
  const recentlyReleasedGames = getRecentlyReleasedGames();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section - Level 4 Games Slider */}
      {level4Games.length > 0 && (
        <div className="w-screen -mx-4 container mx-auto px-4 pt-8">
          <div className="relative">
            <div
              className="overflow-hidden rounded-custom-paddings p-0"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {level4Games.map((game, index) => (
                  <div key={game.id} className="w-full flex-shrink-0 relative">
                    <div className="flex flex-col md:flex-row md:h-[60vh] rounded-custom-paddings p-0 overflow-hidden bg-card">
                      {/* Image de couverture - 70% sur desktop */}
                      <div
                        className="relative w-full min-h-[200px] md:w-[70%] md:h-[40vh] bg-cover bg-center cursor-pointer rounded-custom-paddings md-m-10-nor md-h-semi-full overflow-hidden"
                        style={{ backgroundImage: `url(${game.cover_image_url})` }}
                        onClick={() => navigate(`/game/${game.id}`)}
                      >
                        {/* <div className="absolute inset-0 bg-black/30"></div> */}
                      </div>

                      {/* Sidebar - 30% sur desktop */}
                      <div className="w-full md:w-[30%] bg-card p-4 md:p-6 flex flex-col justify-center">
                        {/* 4 Screenshots en grille 2x2 */}
                        {game.screenshots && game.screenshots.length > 0 && (
                          <div className="grid grid-cols-2 gap-2 mb-4">
                            {game.screenshots.slice(0, 4).map((screenshot, idx) => (
                              <div
                                key={idx}
                                className="relative aspect-video overflow-hidden rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => navigate(`/game/${game.id}`)}
                              >
                                <img
                                  src={screenshot}
                                  alt={`${game.title} screenshot ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Titre */}
                        <h2
                          className="text-xl md:text-3xl font-bold mb-3 cursor-pointer hover:text-primary transition-colors line-clamp-2"
                          onClick={() => navigate(`/game/${game.id}`)}
                        >
                          {game.title}
                        </h2>

                        {/* Tags */}
                        {game.genres && game.genres.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-4">
                            {game.genres.slice(0, 4).map((genre, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {genre}
                              </Badge>
                            ))}
                            {game.genres.length > 4 && (
                              <Badge variant="outline" className="text-xs">
                                +{game.genres.length - 4}
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* Bouton Request */}
                        {/* <div onClick={(e) => e.stopPropagation()}>
                          <GameRequestButton
                            gameId={game.id}
                            gameTitle={game.title}
                            allowRequests={game.allow_requests}
                            availableKeys={game.available_keys}
                            onRequestClick={() => handleRequestKey(game)}
                          />
                        </div> */}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bullet points avec progression - Mobile en dessous, Desktop en position absolue */}
            {level4Games.length > 1 && (
              <>
                {/* Mobile: en dessous du slider */}
                <div className="md:hidden flex justify-center gap-2 mt-4 pb-2">
                  {level4Games.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentSlide(index);
                        setSlideProgress(0);
                      }}
                      className="relative h-3 w-8 border border-white/20 bg-white/20 rounded-full overflow-hidden transition-all hover:bg-white/30 backdrop-blur-sm"
                      aria-label={`Go to slide ${index + 1}`}
                    >
                      <div
                        className={`absolute inset-0 rounded-full transition-all ${index === currentSlide ? 'bg-white' : index < currentSlide ? 'bg-white/60' : 'bg-white/40'
                          }`}
                        style={{
                          width: index === currentSlide ? `${slideProgress}%` : index < currentSlide ? '100%' : '0%'
                        }}
                      />
                    </button>
                  ))}
                </div>

                {/* Desktop: en position absolue */}
                <div className="hidden md:flex absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 gap-2">
                  {level4Games.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentSlide(index);
                        setSlideProgress(0);
                      }}
                      className="relative h-3 w-8 border border-white/20 bg-white/20 rounded-full overflow-hidden transition-all hover:bg-white/30 backdrop-blur-sm"
                      aria-label={`Go to slide ${index + 1}`}
                    >
                      <div
                        className={`absolute inset-0 rounded-full transition-all ${index === currentSlide ? 'bg-white' : index < currentSlide ? 'bg-white/60' : 'bg-white/40'
                          }`}
                        style={{
                          width: index === currentSlide ? `${slideProgress}%` : index < currentSlide ? '100%' : '0%'
                        }}
                      />
                    </button>
                  ))}
                </div>
              </>
            )}

            {/*level4Games.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute left-2 md:left-8 top-1/2 transform -translate-y-1/2 bg-white/10 border-white/30 text-white hover:bg-white/20 z-20"
                  onClick={prevSlide}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute right-2 md:right-8 top-1/2 transform -translate-y-1/2 bg-white/10 border-white/30 text-white hover:bg-white/20 z-20"
                  onClick={nextSlide}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </>
            )*/}
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 pt-8">
        <EventBanner />
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Featured Games - Level 3 */}
        {level3Games.length > 0 && (
          <div ref={featuredRef} className="mb-12">
            <h2 className={`text-3xl font-bold mb-6 transition-all duration-700 ${isFeaturedVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>Featured Games</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {level3Games.slice(0, 6).map((game, index) => (
                <Card
                  key={game.id}
                  className={`overflow-hidden hover:shadow-lg transition-all duration-700 rounded-custom-paddings  ${isFeaturedVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div
                    className="relative cursor-pointer"
                    onClick={() => navigate(`/game/${game.id}`)}
                  >
                    <img
                      src={game.cover_image_url}
                      alt={game.title}
                      className="w-full h-48 object-cover rounded-custom-paddings p-0"
                    />
                  </div>
                  <div className="p-4">
                    <h3
                      className="text-xl font-semibold mb-2 cursor-pointer hover:text-primary"
                      onClick={() => navigate(`/game/${game.id}`)}
                    >
                      {game.title}
                    </h3>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {game.genres.slice(0, 5).map((genre, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {genre}
                        </Badge>
                      ))}
                      {game.genres.length > 5 && (
                        <Badge variant="outline" className="text-xs">
                          +{game.genres.length - 5}
                        </Badge>
                      )}
                    </div>
                    {/* <GameRequestButton
                      gameId={game.id}
                      gameTitle={game.title}
                      allowRequests={game.allow_requests}
                      availableKeys={game.available_keys}
                      onRequestClick={() => handleRequestKey(game)}
                    /> */}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Trending - Level 2 */}
        {level2Games.length > 0 && (
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6">Highlights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {level2Games.slice(0, 8).map((game) => (
                <Card key={game.id} className="overflow-hidden hover:shadow-lg transition-shadow rounded-custom-paddings">
                  <div
                    className="relative cursor-pointer"
                    onClick={() => navigate(`/game/${game.id}`)}
                  >
                    <img
                      src={game.cover_image_url}
                      alt={game.title}
                      className="w-full h-32 object-cover rounded-custom-paddings p-0"
                    />
                  </div>
                  <div className="p-3">
                    <h3
                      className="font-semibold mb-1 text-sm line-clamp-1 cursor-pointer hover:text-primary"
                      onClick={() => navigate(`/game/${game.id}`)}
                    >
                      {game.title}
                    </h3>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {game.genres.slice(0, 3).map((genre, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {genre}
                        </Badge>
                      ))}
                      {game.genres.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{game.genres.length - 3}
                        </Badge>
                      )}
                    </div>
                    {/* <GameRequestButton
                      gameId={game.id}
                      gameTitle={game.title}
                      allowRequests={game.allow_requests}
                      availableKeys={game.available_keys}
                      onRequestClick={() => handleRequestKey(game)}
                    /> */}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Coming Soon & Recently Released - Two columns like screenshot */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Coming Soon */}
          <div ref={comingSoonRef}>
            <h2 className={`text-lg font-semibold text-gray-700 mb-4 uppercase tracking-wide transition-all duration-700 ${isComingSoonVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>Coming Soon</h2>
            <div className="space-y-3">
              {comingSoonGames.slice(0, 7).map((game, index) => (
                <Card
                  key={game.id}
                  className={`p-3 hover:shadow-md transition-all duration-700 rounded-lg ${isComingSoonVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  <div className="flex flex-wrap gap-3">
                    <img
                      src={game.cover_image_url}
                      alt={game.title}
                      className="w-32 h-16 object-cover rounded"
                      onClick={() => navigate(`/game/${game.id}`)}
                      style={{ cursor: 'pointer' }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3
                          className="font-medium text-sm line-clamp-2 cursor-pointer hover:text-primary flex-1"
                          onClick={() => navigate(`/game/${game.id}`)}
                        >
                          {game.title}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {game.release_date ? formatDate(game.release_date) : 'No release date'}
                        </p>
                      </div>
                      {/* <div className="flex-shrink-0 w-full sm:w-auto">
                        <GameRequestButton
                          gameId={game.id}
                          gameTitle={game.title}
                          allowRequests={game.allow_requests}
                          availableKeys={game.available_keys}
                          onRequestClick={() => handleRequestKey(game)}
                        />
                      </div> */}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <Button
              variant="outline"
              className="w-full mt-4 text-sm"
              onClick={() => navigate('/games')}
            >
              See All
            </Button>
          </div>

          {/* Recently Released */}
          <div ref={recentlyRef}>
            <h2 className={`text-lg font-semibold text-gray-700 mb-4 uppercase tracking-wide transition-all duration-700 ${isRecentlyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>Recently Released</h2>
            <div className="space-y-3">
              {recentlyReleasedGames.slice(0, 7).map((game, index) => (
                <Card
                  key={game.id}
                  className={`p-3 hover:shadow-md transition-all duration-700 rounded-lg ${isRecentlyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  <div className="flex flex-wrap gap-3">
                    <img
                      src={game.cover_image_url}
                      alt={game.title}
                      className="w-32 h-16 object-cover rounded"
                      onClick={() => navigate(`/game/${game.id}`)}
                      style={{ cursor: 'pointer' }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col items-start justify-between gap-2 mb-1">
                        <h3
                          className="font-medium text-sm line-clamp-2 cursor-pointer hover:text-primary flex-1"
                          onClick={() => navigate(`/game/${game.id}`)}
                        >
                          {game.title}
                        </h3>

                        <p className="text-xs text-muted-foreground">
                          {game.release_date ? formatDate(game.release_date) : 'No release date'}
                        </p>
                      </div>
                    </div>
                    {/* <div className="flex-shrink-0 w-full sm:w-auto">
                      <GameRequestButton
                        gameId={game.id}
                        gameTitle={game.title}
                        allowRequests={game.allow_requests}
                        availableKeys={game.available_keys}
                        onRequestClick={() => handleRequestKey(game)}
                      />
                    </div> */}
                  </div>
                </Card>
              ))}
            </div>
            <Button
              variant="outline"
              className="w-full mt-4 text-sm"
              onClick={() => navigate('/games')}
            >
              See All
            </Button>
          </div>
        </div>

      </div>

      {/* Request Key Dialog */}
      <Dialog open={isRequestingKey} onOpenChange={setIsRequestingKey}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Key for {selectedGame?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="message">Message (optional)</Label>
              <Textarea
                id="message"
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                placeholder="Tell the developer why you'd like a key for this game..."
                rows={4}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={submitKeyRequest}
                disabled={isSubmittingRequest}
                className="flex-1"
              >
                {isSubmittingRequest ? "Submitting..." : "Submit Request"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsRequestingKey(false)}
                disabled={isSubmittingRequest}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}